import { AfterViewInit, Component, computed, inject, signal, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IStudentEnrollments } from '../../../../../interfaces/student';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { FlashService } from '../../../../../services/flash';
import { StudentService } from '../../../../../services/student';
import { IClass } from '../../../../../interfaces/class';
import { AcademicTermService } from '../../../../../services/academic-term';
import { FormsModule, NgForm } from '@angular/forms';
import { AttendanceService } from '../../../../../services/attendance';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { AcademicYearService } from '../../../../../services/academic-year';

interface AttendanceRecordInput {
  enrollment_id: string;
  attendance_status: 'PRESENT' | 'ABSENT';
  remarks?: string;
}

interface BulkAttendancePayload {
  term_id: string;
  attendance_date: string;
  records: AttendanceRecordInput[];
}

@Component({
  selector: 'app-attendance-register',
  imports: [FormsModule, LoadingIndicator],
  templateUrl: './attendance-register.html',
  styleUrl: './attendance-register.css',
})
export class AttendanceRegister implements AfterViewInit {
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private studentService: StudentService,
    protected academicTermService: AcademicTermService,
    private attendanceService: AttendanceService,
    protected academicYearService: AcademicYearService,
  ) {
    this.route.queryParamMap.subscribe((params) => {
      this.classId = params.get('id')!;
    });

    if (!this.classId) {
      void this.router.navigate(['/dashboard/class']);
    }

    this.getAcademicYears();
    this.getAcademicTerms();
  }

  ngAfterViewInit() {
    this.attendanceForm()?.form.valueChanges.subscribe((values) => {
      this.formValues.set(values);
    });
  }

  protected classId!: string;
  protected classStudents = signal<IStudentEnrollments | null>(null);
  protected readonly flashService = inject(FlashService);
  protected class = signal<IClass | null>(null);
  protected termId!: string;
  protected attendanceDate!: string;
  protected attendanceForm = viewChild<NgForm>('attendanceForm');
  private formValues = signal<Record<string, any>>({});
  protected totalStudents = computed(() => this.classStudents()?.payload?.length ?? 0);
  protected todayDate = new Date().toISOString().split('T')[0];
  protected isSubmitting = signal(false);
  protected currentAcademicYear = signal<string>('');

  protected markedCount = computed(() => {
    const values = this.formValues();
    return Object.keys(values).filter((key) => key.startsWith('status') && !!values[key]).length;
  });

  protected presentCount = computed(() => {
    const values = this.formValues();
    return Object.values(values).filter((v) => v === 'PRESENT').length;
  });

  protected absentCount = computed(() => {
    const values = this.formValues();
    return Object.values(values).filter((v) => v === 'ABSENT').length;
  });

  protected percentMarked = computed(() => {
    const total = this.totalStudents();
    if (!total) return 0;
    return Math.round((this.markedCount() / total) * 100);
  });

  protected getAcademicYears(): void {
    const params: Record<string, boolean | number> = {
      page: 0,
      size: 5,
      current_academic_year: true,
    };

    this.academicYearService
      .getAcademicYears(`${ApiEndpoints.url}/academic-year`, params)
      .subscribe({
        next: (res) => {
          this.currentAcademicYear.set(res.payload[0].id);
          this.getClassStudents();
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load Dashboard. Refresh the page.',
          );
        },
      });
  }

  protected getClassStudents(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 1000,
      class_id: this.classId,
      academic_year_id: this.currentAcademicYear(),
      status: 'ACTIVE'
    };

    this.studentService
      .getStudentEnrollments(`${ApiEndpoints.url}/student-enrollment`, params)
      .subscribe({
        next: (data) => {
          this.classStudents.set(data);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load students. Refresh the page',
          );
        },
      });
  }

  protected getAcademicTerms(): void {
    const params: Record<string, boolean | number> = {
      page: 0,
      size: 1,
      current_term: true,
    };

    this.academicTermService
      .getAcademicTerms(`${ApiEndpoints.url}/academic-term`, params)
      .subscribe({
        next: (res) => {
          this.termId = res.payload[0].id;
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load Academic Terms. Refresh the page.',
          );
        },
      });
  }

  protected submitAttendance(form: NgForm) {
    const students = this.classStudents()?.payload ?? [];
    const formValue = form.value;

    if (!formValue.attendanceDate) {
      this.flashService.failure('Please select a date.');
      return;
    }

    const attendanceDate = new Date(`${formValue.attendanceDate}T00:00:00`);
    const day = attendanceDate.getDay();

    if (day === 0 || day === 6) {
      this.flashService.failure(
        'Attendance cannot be recorded on weekends. Please select a weekday.',
      );
      return;
    }

    const records: AttendanceRecordInput[] = [];
    const unmarked: number[] = [];

    students.forEach((student: any, index: number) => {
      const status = formValue[`status${index}`];

      if (!status) {
        unmarked.push(index);
        return;
      }

      records.push({
        enrollment_id: student.id,
        attendance_status: status,
      });
    });

    if (unmarked.length) {
      this.flashService.failure(`${unmarked.length} student(s) still unmarked.`);
      return;
    }

    const payload: BulkAttendancePayload = {
      term_id: this.termId,
      attendance_date: formValue.attendanceDate,
      records,
    };

    this.isSubmitting.set(true);

    this.attendanceService
      .createAttendance(`${ApiEndpoints.url}/attendance-record`, payload)
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);
          this.flashService.success('Recorded class attendance successfully');
          form.resetForm();
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Failed to save attendance. Please try again.',
          );
        },
      });
  }

  protected markAll(status: 'PRESENT' | 'ABSENT', form: NgForm) {
    const students = this.classStudents()?.payload ?? [];

    students.forEach((_: any, index: number) => {
      const control = form.controls[`status${index}`];
      if (control) {
        control.setValue(status);
      }
    });
  }
}
